import React, { useState, useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import styled from 'styled-components';
import {
  ArrowLeft,
  ArrowRight,
  Camera,
  AlertCircle,
  CheckCircle,
  QrCode,
  User,
  Plus,
  X,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { BrowserMultiFormatReader } from '@zxing/library';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { frameDisplayName, OWN_FRAME_NAME } from '../../utils/itemDisplayName';
import { isTwoPairSale, pairSessionKey } from '../../utils/salePairs';
import PairBanner from '../../components/PairBanner';

const Container = styled.div`
  min-height: 100vh;
  background: ${props => props.theme.colors.background};
  padding: ${props => props.theme.spacing.md} ${props => props.theme.spacing.lg};
`;

const Header = styled.div`
  max-width: 1200px;
  margin: 0 auto ${props => props.theme.spacing.md} auto;
`;

const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${props => props.theme.spacing.xs};
  padding: 0.4rem 0.75rem;
  background: ${props => props.theme.colors.white};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  color: ${props => props.theme.colors.textSecondary};
  font-size: ${props => props.theme.fontSizes.sm};
  cursor: pointer;
  transition: all ${props => props.theme.transitions.fast};

  &:hover {
    background: ${props => props.theme.colors.gray50};
    color: ${props => props.theme.colors.textPrimary};
    border-color: ${props => props.theme.colors.primary};
  }
`;

// Two-pair sale: pair 1 and pair 2 side by side.
const PairColumns = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: ${props => props.theme.spacing.lg};
  align-items: start;

  @media (max-width: 1000px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const PairColumn = styled.section`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.sm};
`;

const PairColumnHead = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  flex-wrap: wrap;

  span:last-child { font-size: 0.85rem; color: #64748b; }
`;

const PairContinueRow = styled.div`
  max-width: 1400px;
  margin: ${props => props.theme.spacing.lg} auto 0;
  display: flex;
  justify-content: flex-end;
`;

const PageContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.md};
`;

const ContentGrid = styled.div`
  width: 100%;
  max-width: 520px;
  margin: 0 auto;
`;

const CardHeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${props => props.theme.spacing.sm};
  margin: 0 0 ${props => props.theme.spacing.sm} 0;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid ${props => props.theme.colors.border};
`;

const CardTitle = styled.h2`
  font-size: ${props => props.theme.fontSizes.base};
  font-weight: ${props => props.theme.fontWeights.semibold};
  color: ${props => props.theme.colors.textPrimary};
  margin: 0;
`;

const StatusPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.2rem 0.55rem;
  font-size: ${props => props.theme.fontSizes.xs};
  font-weight: ${props => props.theme.fontWeights.semibold};
  border-radius: 999px;
  background: ${props => props.$tone === 'success'
    ? 'rgba(16, 185, 129, 0.12)'
    : props.$tone === 'info'
      ? 'rgba(99, 102, 241, 0.12)'
      : props.theme.colors.gray100};
  color: ${props => props.$tone === 'success'
    ? '#047857'
    : props.$tone === 'info'
      ? '#4f46e5'
      : props.theme.colors.textSecondary};
`;

const ScanAnotherButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.4rem 0.7rem;
  background: ${props => props.theme.colors.white};
  color: ${props => props.theme.colors.primary};
  border: 1px dashed ${props => props.theme.colors.primary};
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: ${props => props.theme.fontSizes.sm};
  font-weight: ${props => props.theme.fontWeights.medium};
  cursor: pointer;
  transition: all ${props => props.theme.transitions.fast};

  &:hover {
    background: ${props => props.theme.colors.primary}10;
  }
`;

const FrameDetailsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(115px, 1fr));
  gap: 0.5rem 0.85rem;
  margin-bottom: ${props => props.theme.spacing.sm};
`;

const AdjustmentsRow = styled.section`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  padding-top: ${props => props.theme.spacing.lg};
  border-top: 2px solid ${props => props.theme.colors.border};
`;

const AdjustmentsPanel = styled.div`
  width: 100%;
`;

const AdjustmentsInlineBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.lg};
  width: 100%;
`;

const AdjustmentsTitle = styled.h3`
  font-size: ${props => props.theme.fontSizes.lg};
  font-weight: ${props => props.theme.fontWeights.semibold};
  color: ${props => props.theme.colors.textPrimary};
  margin: 0;
  flex: 0 0 auto;
  line-height: 1.3;
`;

const AdjustmentsChecks = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.lg};
  flex: 1 1 200px;
`;

const AdjustmentField = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
`;

const AdjustmentLabel = styled.label`
  font-size: ${props => props.theme.fontSizes.sm};
  font-weight: ${props => props.theme.fontWeights.medium};
  color: ${props => props.theme.colors.textPrimary};
  line-height: 1.2;
`;

const AdjustmentInput = styled.input`
  width: 104px;
  padding: 0.5rem 0.6rem;
  border: 1px solid ${props => (props.$invalid ? '#dc2626' : props.theme.colors.border)};
  border-radius: 9px;
  font-size: ${props => props.theme.fontSizes.sm};
  color: ${props => (props.$invalid ? '#dc2626' : props.theme.colors.textPrimary)};
  font-weight: ${props => (props.$invalid ? props.theme.fontWeights.bold : 'inherit')};
  text-align: center;
  background: ${props => (props.$invalid ? '#fef2f2' : props.theme.colors.white)};

  &::placeholder {
    color: #9ca3af;
  }

  &:focus {
    outline: none;
    border-color: ${props => (props.$invalid ? '#dc2626' : '#6366f1')};
    box-shadow: 0 0 0 3px ${props => (props.$invalid ? 'rgba(220, 38, 38, 0.15)' : 'rgba(99, 102, 241, 0.12)')};
  }
`;

const AdjustmentWarning = styled.div`
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  margin-top: 6px;
  padding: 4px 8px;
  border-radius: 6px;
  background: #dc2626;
  color: #fff;
  font-size: 0.68rem;
  font-weight: 700;
  white-space: nowrap;
  z-index: 5;

  &::before {
    content: '';
    position: absolute;
    bottom: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 5px solid transparent;
    border-bottom-color: #dc2626;
  }
`;

const AdjustToggle = styled.label`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.35rem;
  min-height: 38px;
  padding: 0.45rem 0.8rem;
  border-radius: 9px;
  border: 1px solid ${props => (props.$checked ? '#16a34a' : props.theme.colors.border)};
  background: ${props => (props.$checked ? '#16a34a' : props.theme.colors.white)};
  color: ${props => (props.$checked ? '#ffffff' : props.theme.colors.textSecondary)};
  font-size: ${props => props.theme.fontSizes.sm};
  font-weight: 700;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    border-color: #16a34a;
  }

  input {
    position: absolute;
    opacity: 0;
    width: 0;
    height: 0;
  }
`;

const ContinueRow = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
  padding-bottom: ${props => props.theme.spacing.lg};
`;

const ScannerSection = styled.div`
  background: ${props => props.theme.colors.white};
  border-radius: ${props => props.theme.borderRadius.lg};
  padding: ${props => props.theme.spacing.md};
  box-shadow: ${props => props.theme.shadows.sm};
  border: 1px solid ${props => props.theme.colors.border};
`;

const SectionHelper = styled.p`
  font-size: ${props => props.theme.fontSizes.sm};
  color: ${props => props.theme.colors.textSecondary};
  margin: 0 0 ${props => props.theme.spacing.md} 0;
`;

const ScannerContainer = styled.div`
  position: relative;
  width: 100%;
  height: 180px;
  max-width: 260px;
  margin: 0 auto ${props => props.theme.spacing.sm} auto;
  background: ${props => props.theme.colors.gray50};
  border: 2px dashed ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  cursor: ${props => props.isScanning ? 'default' : 'pointer'};
  transition: all ${props => props.theme.transitions.fast};

  &:hover {
    border-color: ${props => props.isScanning ? props.theme.colors.border : props.theme.colors.primary};
    background: ${props => props.isScanning ? props.theme.colors.gray50 : props.theme.colors.gray100};
  }
`;

const Video = styled.video`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: ${props => props.isScanning ? 'block' : 'none'};
`;

const ScannerPlaceholder = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  text-align: center;
  padding: ${props => props.theme.spacing.sm};
`;

const ScannerIcon = styled.div`
  width: 44px;
  height: 44px;
  border-radius: ${props => props.theme.borderRadius.full};
  background: ${props => props.theme.colors.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.theme.colors.white};
`;

const ScannerText = styled.p`
  font-size: ${props => props.theme.fontSizes.sm};
  font-weight: ${props => props.theme.fontWeights.medium};
  color: ${props => props.theme.colors.textPrimary};
  margin: 0;
`;

const ScannerSubtext = styled.p`
  font-size: ${props => props.theme.fontSizes.sm};
  color: ${props => props.theme.colors.textSecondary};
  margin: 0;
`;

const ErrorMessage = styled.div`
  background: #fee2e2;
  color: #dc2626;
  padding: ${props => props.theme.spacing.sm};
  border-radius: ${props => props.theme.borderRadius.md};
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  margin-bottom: ${props => props.theme.spacing.sm};
  font-size: ${props => props.theme.fontSizes.sm};
`;

const SuccessMessage = styled.div`
  background: #d1fae5;
  color: #059669;
  padding: ${props => props.theme.spacing.sm};
  border-radius: ${props => props.theme.borderRadius.md};
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  margin-bottom: ${props => props.theme.spacing.sm};
  font-size: ${props => props.theme.fontSizes.sm};
`;

const FitWarning = styled.div`
  background: #fffbeb;
  border: 1px solid #f59e0b;
  color: #92400e;
  padding: 0.85rem 1rem;
  border-radius: ${props => props.theme.borderRadius.md};
  display: flex;
  align-items: flex-start;
  gap: 0.6rem;
  margin-bottom: ${props => props.theme.spacing.md};
  font-size: 0.9rem;
  line-height: 1.35;

  strong { display: block; margin-bottom: 0.15rem; }
`;

const OutOfStockBanner = styled.div`
  background: #fee2e2;
  border: 1px solid #dc2626;
  color: #991b1b;
  padding: 0.85rem 1rem;
  border-radius: ${props => props.theme.borderRadius.md};
  display: flex;
  align-items: flex-start;
  gap: 0.6rem;
  margin-bottom: ${props => props.theme.spacing.md};
  font-size: 0.9rem;
  line-height: 1.35;

  strong { display: block; margin-bottom: 0.15rem; }
`;

// A scanned frame with no units left must not be sellable.
const isFrameOutOfStock = (frame) =>
  !!frame && !frame.hasOwnFrame && frame.stockQuantity !== undefined && Number(frame.stockQuantity) <= 0;

// Cross-check the chosen frame's target audience (MALE / FEMALE / UNISEX / ENFANT)
// against the client's saved gender + age group. Returns an i18n key or null.
function frameFitWarningKey(frame, gender, ageGroup) {
  const sex = String(frame?.sex || '').toUpperCase();
  if (!sex) return null;
  const isChild = ageGroup === 'child';
  if (sex === 'ENFANT' && ageGroup && !isChild) return 'frameSelection.fitWarning.kidsFrameNonChild';
  if (isChild && (sex === 'MALE' || sex === 'FEMALE')) return 'frameSelection.fitWarning.adultFrameChild';
  if (sex === 'MALE' && gender === 'female') return 'frameSelection.fitWarning.mensFrameFemale';
  if (sex === 'FEMALE' && gender === 'male') return 'frameSelection.fitWarning.womensFrameMale';
  return null;
}

const FrameDetail = styled.div`
  margin-bottom: 0;
`;

const FrameDetailLabel = styled.div`
  font-size: ${props => props.theme.fontSizes.xs};
  color: ${props => props.theme.colors.textSecondary};
  margin-bottom: 0.15rem;
`;

const FrameDetailValue = styled.div`
  font-size: ${props => props.theme.fontSizes.sm};
  font-weight: ${props => props.theme.fontWeights.medium};
  color: ${props => props.theme.colors.textPrimary};
`;

const FramePrice = styled.div`
  font-size: ${props => props.theme.fontSizes.lg};
  font-weight: ${props => props.theme.fontWeights.bold};
  color: ${props => props.theme.colors.primary};
  margin: ${props => props.theme.spacing.sm} 0 0 0;
  padding: 0.45rem ${props => props.theme.spacing.sm};
  background: ${props => props.theme.colors.primaryLight}10;
  border-radius: ${props => props.theme.borderRadius.md};
  text-align: center;
`;

const NextButton = styled.button`
  min-width: 260px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${props => props.theme.spacing.sm};
  padding: ${props => props.theme.spacing.md} ${props => props.theme.spacing.lg};
  background: ${props => props.theme.colors.primary};
  color: ${props => props.theme.colors.white};
  border: none;
  border-radius: ${props => props.theme.borderRadius.md};
  font-weight: ${props => props.theme.fontWeights.medium};
  cursor: pointer;
  transition: all ${props => props.theme.transitions.fast};

  &:hover:not(:disabled) {
    background: ${props => props.theme.colors.primaryDark};
  }

  &:disabled {
    background: ${props => props.theme.colors.gray300};
    cursor: not-allowed;
  }
`;

const StopButton = styled.button`
  width: 100%;
  padding: ${props => props.theme.spacing.md};
  background: ${props => props.theme.colors.gray200};
  color: ${props => props.theme.colors.textPrimary};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  font-weight: ${props => props.theme.fontWeights.medium};
  cursor: pointer;
  transition: all ${props => props.theme.transitions.fast};
  margin-top: ${props => props.theme.spacing.md};

  &:hover {
    background: ${props => props.theme.colors.gray300};
  }
`;

const ManualInputContainer = styled.div`
  margin-top: ${props => props.theme.spacing.sm};
  padding: ${props => props.theme.spacing.sm};
  background: ${props => props.theme.colors.gray50};
  border-radius: ${props => props.theme.borderRadius.md};
  border: 1px solid ${props => props.theme.colors.border};
`;

const ManualInputRow = styled.form`
  display: flex;
  gap: ${props => props.theme.spacing.sm};
  align-items: stretch;
`;

const ManualInput = styled.input`
  flex: 1;
  padding: 0.45rem 0.65rem;
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: ${props => props.theme.fontSizes.sm};
  color: ${props => props.theme.colors.textPrimary};
  background: ${props => props.theme.colors.white};
  transition: all ${props => props.theme.transitions.fast};

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
    box-shadow: 0 0 0 3px ${props => props.theme.colors.primary}20;
  }

  &::placeholder {
    color: ${props => props.theme.colors.textSecondary};
  }
`;

const ManualInputButton = styled.button`
  padding: 0.45rem 0.85rem;
  background: ${props => props.theme.colors.primary};
  color: ${props => props.theme.colors.white};
  border: none;
  border-radius: ${props => props.theme.borderRadius.md};
  font-weight: ${props => props.theme.fontWeights.medium};
  font-size: ${props => props.theme.fontSizes.sm};
  cursor: pointer;
  transition: all ${props => props.theme.transitions.fast};
  white-space: nowrap;

  &:hover:not(:disabled) {
    background: ${props => props.theme.colors.primaryDark};
  }

  &:disabled {
    background: ${props => props.theme.colors.gray300};
    cursor: not-allowed;
  }
`;

const ToggleManualButton = styled.button`
  width: 100%;
  padding: 0.45rem;
  background: transparent;
  color: ${props => props.theme.colors.primary};
  border: 1px dashed ${props => props.theme.colors.primary};
  border-radius: ${props => props.theme.borderRadius.md};
  font-weight: ${props => props.theme.fontWeights.medium};
  font-size: ${props => props.theme.fontSizes.sm};
  cursor: pointer;
  transition: all ${props => props.theme.transitions.fast};

  &:hover {
    background: ${props => props.theme.colors.primary}10;
  }
`;

const OwnFrameCard = styled.div`
  margin-top: ${props => props.theme.spacing.md};
  padding: 0;
  border: 2px solid ${props => props.$active ? '#10b981' : props.theme.colors.border};
  background: ${props => props.$active ? '#f0fdf4' : props.theme.colors.white};
  border-radius: ${props => props.theme.borderRadius.md};
  overflow: hidden;
  transition: all ${props => props.theme.transitions.fast};
`;

const OwnFrameToggle = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  padding: ${props => props.theme.spacing.sm};
  border: none;
  background: transparent;
  cursor: pointer;
  text-align: left;
`;

const OwnFrameToggleIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: ${props => props.theme.borderRadius.full};
  background: ${props => props.$active ? '#10b981' : props.theme.colors.gray100};
  color: ${props => props.$active ? props.theme.colors.white : props.theme.colors.textSecondary};
  flex-shrink: 0;
  transition: all ${props => props.theme.transitions.fast};
`;

const OwnFrameToggleText = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.1rem;

  strong {
    font-size: ${props => props.theme.fontSizes.sm};
    font-weight: ${props => props.theme.fontWeights.semibold};
    color: ${props => props.theme.colors.textPrimary};
  }

  span {
    font-size: ${props => props.theme.fontSizes.xs};
    color: ${props => props.theme.colors.textSecondary};
  }
`;

const OwnFrameSwitch = styled.span`
  position: relative;
  width: 32px;
  height: 18px;
  border-radius: 999px;
  background: ${props => props.$active ? '#10b981' : '#cbd5e1'};
  flex-shrink: 0;
  transition: all 0.2s;

  &::after {
    content: '';
    position: absolute;
    top: 2px;
    left: ${props => props.$active ? '16px' : '2px'};
    width: 14px;
    height: 14px;
    border-radius: 50%;
    background: #fff;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
    transition: all 0.2s;
  }
`;

const OwnFrameDetails = styled.div`
  padding: 0 ${props => props.theme.spacing.sm} ${props => props.theme.spacing.sm};
`;

const ConditionSection = styled.div`
  margin-top: ${props => props.theme.spacing.md};
  padding: ${props => props.theme.spacing.sm};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  background: ${props => props.theme.colors.white};
`;

const ConditionTitle = styled.h4`
  margin: 0 0 ${props => props.theme.spacing.sm} 0;
  font-size: ${props => props.theme.fontSizes.sm};
  font-weight: ${props => props.theme.fontWeights.semibold};
  color: ${props => props.theme.colors.textPrimary};
`;

const ConditionList = styled.div`
  display: grid;
  gap: 0.45rem;
`;

const ConditionHeader = styled.div`
  display: grid;
  grid-template-columns: 58px minmax(0, 1fr) 58px;
  gap: 0.5rem;
  align-items: center;
  font-size: ${props => props.theme.fontSizes.xs};
  font-weight: 700;
  color: ${props => props.theme.colors.textSecondary};
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;


const ConditionChoiceRow = styled.div`
  display: grid;
  grid-template-columns: 58px minmax(0, 1fr) 58px;
  gap: 0.5rem;
  align-items: center;
  padding: 0.45rem 0;
  border-bottom: 1px solid ${props => props.theme.colors.border};

  &:last-child {
    border-bottom: 0;
  }
`;

const ChoiceButton = styled.button`
  min-height: 34px;
  border-radius: 9px;
  border: 1px solid ${props => props.$active ? '#6366f1' : props.theme.colors.border};
  background: ${props => props.$active ? '#eef2ff' : props.theme.colors.white};
  color: ${props => props.$active ? '#3730a3' : props.theme.colors.textSecondary};
  font-weight: 700;
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    border-color: #6366f1;
  }
`;

const ConditionLabel = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  text-align: center;
  font-size: ${props => props.theme.fontSizes.sm};
  color: ${props => props.theme.colors.textPrimary};
`;

const CriticalBadge = styled.span`
  font-size: ${props => props.theme.fontSizes.xs};
  color: #b91c1c;
  background: #fee2e2;
  padding: 0.1rem 0.4rem;
  border-radius: 999px;
  font-weight: 600;
`;

const RiskBannerBase = styled.div`
  margin-top: ${props => props.theme.spacing.sm};
  padding: ${props => props.theme.spacing.sm};
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: ${props => props.theme.fontSizes.sm};
  font-weight: ${props => props.theme.fontWeights.medium};
`;

const RiskBanner = styled(RiskBannerBase)`
  background: #fffbeb;
  color: #b45309;
  border: 1px solid #fcd34d;
`;

const HighRiskBanner = styled(RiskBannerBase)`
  background: #fff7ed;
  color: #c2410c;
  border: 1px solid #fdba74;
`;

const RejectBanner = styled(RiskBannerBase)`
  background: #fef2f2;
  color: #b91c1c;
  border: 1px solid #fecaca;
`;

// ── Extra frames: frame-only add-ons (spares/backups), no lens attached ────
const ExtraFramesCard = styled.div`
  margin-top: ${props => props.theme.spacing.md};
  background: ${props => props.theme.colors.white};
  border-radius: ${props => props.theme.borderRadius.lg};
  padding: ${props => props.theme.spacing.md};
  box-shadow: ${props => props.theme.shadows.sm};
  border: 1px solid ${props => props.theme.colors.border};
`;

const ExtraFrameList = styled.div`
  margin-bottom: ${props => props.theme.spacing.sm};
`;

const ExtraFrameRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.55rem 0;
  border-bottom: 1px solid ${props => props.theme.colors.border};

  &:last-child {
    border-bottom: none;
  }
`;

const ExtraFrameInfo = styled.div`
  flex: 1 1 auto;
  min-width: 0;
`;

const ExtraFrameName = styled.div`
  font-size: ${props => props.theme.fontSizes.sm};
  font-weight: ${props => props.theme.fontWeights.medium};
  color: ${props => props.theme.colors.textPrimary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ExtraFrameMeta = styled.div`
  font-size: ${props => props.theme.fontSizes.xs};
  color: ${props => props.theme.colors.textSecondary};
`;

const ExtraFramePrice = styled.div`
  flex: 0 0 auto;
  font-size: ${props => props.theme.fontSizes.sm};
  font-weight: ${props => props.theme.fontWeights.semibold};
  color: ${props => props.theme.colors.textPrimary};
`;

const RemoveExtraFrameButton = styled.button`
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  background: transparent;
  color: ${props => props.theme.colors.textSecondary};
  border-radius: ${props => props.theme.borderRadius.full};
  cursor: pointer;
  transition: all ${props => props.theme.transitions.fast};

  &:hover {
    background: #fef2f2;
    color: #dc2626;
  }
`;

const AddExtraFrameButton = styled(ToggleManualButton)`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
`;

// One pair's frame: scan / manual code / own frame, extra frames, optical
// adjustments. A one-pair sale shows it as the whole page; a two-pair sale
// ("Both") shows two side by side (`embedded`), each saving under its own
// session keys, with one Continue for both (see FrameSelection below).
const FramePanel = forwardRef(({ pair = 'far', embedded = false, onReadyChange }, ref) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const k = React.useCallback((base) => pairSessionKey(base, pair), [pair]);
  const videoRef = useRef(null);
  const codeReaderRef = useRef(null);
  const streamRef = useRef(null);
  const [isScanning, setIsScanning] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedFrame, setSelectedFrame] = useState(null);
  // Frame-only add-ons (spares/backups) bought alongside the prescription
  // pair — no lens, no adjustments, no fit checks, just their own stock line
  // and price. `isAddingExtraFrame` reuses the SAME scan/manual-entry flow
  // below for the primary frame; fetchFrameByCode branches on it.
  const [extraFrames, setExtraFrames] = useState([]);
  const [isAddingExtraFrame, setIsAddingExtraFrame] = useState(false);
  const [manualCode, setManualCode] = useState('');
  const [showManualInput, setShowManualInput] = useState(false);
  const [hasOwnFrame, setHasOwnFrame] = useState(false);
  const [ownFrameChecks, setOwnFrameChecks] = useState({
    twisted: null,
    appearanceAcceptable: null,
    groovingConsistent: null,
    hingesFunctional: null,
    frameAgeOver2Years: null
  });

  // Temple (branch) and Nose bridge (nez) are simple "adjust" toggles
  const [adjustmentChecks, setAdjustmentChecks] = useState({
    branch: false,
    nez: false
  });

  // Vertex distance, pantoscopic angle and wrap angle are measured numeric values
  const [adjustmentValues, setAdjustmentValues] = useState({
    vertexDistance: '',
    pantoscopicAngle: '',
    wrapAngleGalbe: ''
  });

  // Keep only digits, max 2 characters. Allowed ranges per adjustment (owner
  // spec) are used to flag out-of-range entries, not to silently rewrite
  // what the person typed — a value quietly capped to the limit looks like
  // it was accepted as entered, which hides real fitting mistakes.
  const ADJ_RANGES = {
    vertexDistance: { min: 12, max: 14, unit: 'mm' },
    pantoscopicAngle: { min: 8, max: 12, unit: '°' },
    wrapAngleGalbe: { min: 0, max: 9, unit: '°' },
  };
  const handleAdjustmentValueChange = (key, value) => {
    const digits = value.replace(/\D/g, '').slice(0, 2);
    setAdjustmentValues((prev) => ({ ...prev, [key]: digits }));
  };
  const isAdjustmentOutOfRange = (key) => {
    const range = ADJ_RANGES[key];
    const v = String(adjustmentValues[key] ?? '').trim();
    if (!range || v === '') return false;
    const n = Number(v);
    return n < range.min || n > range.max;
  };

  const numericAdjustmentsFilled =
    adjustmentValues.vertexDistance.trim() !== '' &&
    adjustmentValues.pantoscopicAngle.trim() !== '' &&
    adjustmentValues.wrapAngleGalbe.trim() !== '';

  const numericAdjustmentsInRange =
    !isAdjustmentOutOfRange('vertexDistance') &&
    !isAdjustmentOutOfRange('pantoscopicAngle') &&
    !isAdjustmentOutOfRange('wrapAngleGalbe');

  const allAdjustmentsChecked =
    numericAdjustmentsFilled && numericAdjustmentsInRange && !!adjustmentChecks.branch && !!adjustmentChecks.nez;

  const buildFrameAdjustmentsPayload = () => ({
    vertexDistance: adjustmentValues.vertexDistance,
    pantoscopicAngle: adjustmentValues.pantoscopicAngle,
    wrapAngleGalbe: adjustmentValues.wrapAngleGalbe,
    branch: !!adjustmentChecks.branch,
    nez: !!adjustmentChecks.nez
  });

  const frameConditionChecks = [
    { key: 'twisted', label: t('frameSelection.condition.twisted'), critical: true, riskyAnswer: true, rejectPercent: 40 },
    { key: 'appearanceAcceptable', label: t('frameSelection.condition.appearanceAcceptable'), critical: false, riskyAnswer: false, rejectPercent: 10 },
    { key: 'groovingConsistent', label: t('frameSelection.condition.groovingConsistent'), critical: true, riskyAnswer: false, rejectPercent: 30 },
    { key: 'hingesFunctional', label: t('frameSelection.condition.hingesFunctional'), critical: true, riskyAnswer: false, rejectPercent: 30 },
    { key: 'frameAgeOver2Years', label: t('frameSelection.condition.frameAgeOver2Years'), critical: true, riskyAnswer: true, rejectPercent: 30 }
  ];

  const calculateOwnFrameRisk = () => Math.min(100, frameConditionChecks.reduce((sum, check) => {
    const answer = ownFrameChecks[check.key];
    if (answer === null || answer === undefined) return sum;
    return answer === check.riskyAnswer ? sum + (check.rejectPercent || 0) : sum;
  }, 0));
  const ownFrameRiskPercent = calculateOwnFrameRisk();
  const isOwnFrameRejected = hasOwnFrame && ownFrameRiskPercent > 55;

  // Tracking order functionality removed - use Track Order page in sidebar instead

  // Placeholder lens price (in DZD)
  const lensPrice = 12000;

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);

    // Load saved frame selection data
    const savedFrameDataStr = sessionStorage.getItem(k('frameSelectionData'));
    if (savedFrameDataStr) {
      try {
        const savedData = JSON.parse(savedFrameDataStr);
        if (savedData.selectedFrame) {
          setSelectedFrame(savedData.selectedFrame);
        }
        if (savedData.hasOwnFrame !== undefined) {
          setHasOwnFrame(savedData.hasOwnFrame);
        }
        if (savedData.manualCode) {
          setManualCode(savedData.manualCode);
        }
        if (savedData.ownFrameChecks) {
          setOwnFrameChecks(savedData.ownFrameChecks);
        }
        if (savedData.adjustmentChecks) {
          setAdjustmentChecks((prev) => ({ ...prev, ...savedData.adjustmentChecks }));
        }
        if (savedData.adjustmentValues) {
          setAdjustmentValues((prev) => ({ ...prev, ...savedData.adjustmentValues }));
        }
        if (Array.isArray(savedData.extraFrames)) {
          setExtraFrames(savedData.extraFrames);
        }
      } catch (err) {
        console.error('Error loading saved frame selection data:', err);
      }
    }

    // Also check sessionStorage for selectedFrame
    const savedFrameStr = sessionStorage.getItem(k('selectedFrame'));
    if (savedFrameStr && !selectedFrame) {
      try {
        const savedFrame = JSON.parse(savedFrameStr);
        setSelectedFrame(savedFrame);
      } catch (err) {
        console.error('Error loading saved frame:', err);
      }
    }
  }, []);

  // Auto-save frame selection data
  useEffect(() => {
    const frameSelectionData = {
      selectedFrame,
      hasOwnFrame,
      manualCode,
      ownFrameChecks,
      adjustmentChecks,
      adjustmentValues,
      extraFrames
    };
    const hasAdjustmentValues = Object.values(adjustmentValues).some((v) => String(v).trim() !== '');
    if (selectedFrame || hasOwnFrame || manualCode || Object.values(adjustmentChecks).some(Boolean) || hasAdjustmentValues || extraFrames.length) {
      sessionStorage.setItem(k('frameSelectionData'), JSON.stringify(frameSelectionData));
      if (selectedFrame) {
        sessionStorage.setItem(k('selectedFrame'), JSON.stringify(selectedFrame));
      }
      // Payment reads this key directly (same convention as selectedFrame),
      // rather than digging it out of frameSelectionData.
      sessionStorage.setItem(k('extraFrames'), JSON.stringify(extraFrames));
    }
  }, [k, selectedFrame, hasOwnFrame, manualCode, ownFrameChecks, adjustmentChecks, adjustmentValues, extraFrames]);

  const removeExtraFrame = (index) => {
    setExtraFrames((prev) => prev.filter((_, i) => i !== index));
  };

  const startCamera = async () => {
    try {
      setError('');
      setSuccess('');
      setIsLoading(true);

      if (!videoRef.current) {
        setError('Video element not ready. Please try again.');
        setIsLoading(false);
        return;
      }

      // Stop any existing scanner
      stopCamera();

      // Set scanning state first to make video visible
      setIsScanning(true);

      // Wait for React to update the DOM
      await new Promise(resolve => setTimeout(resolve, 200));

      if (!videoRef.current) {
        setError('Video element not available. Please try again.');
        setIsLoading(false);
        setIsScanning(false);
        return;
      }

      // Make sure video element is visible and properly sized
      videoRef.current.style.display = 'block';
      videoRef.current.style.visibility = 'visible';
      videoRef.current.style.width = '100%';
      videoRef.current.style.height = '100%';
      videoRef.current.style.objectFit = 'cover';
      videoRef.current.setAttribute('autoplay', 'true');
      videoRef.current.setAttribute('playsinline', 'true');
      videoRef.current.setAttribute('muted', 'true');

      // Request camera permission first (helps on Chrome mobile)
      if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
        try {
          const stream = await navigator.mediaDevices.getUserMedia({
            video: {
              facingMode: 'environment' // Prefer back camera
            }
          });
          // Stop this test stream immediately - we just needed permission
          stream.getTracks().forEach(track => track.stop());
        } catch (permError) {
          if (permError.name === 'NotAllowedError' || permError.name === 'PermissionDeniedError') {
            setError('Camera permission denied. Please allow camera access in your browser settings and try again.');
            setIsLoading(false);
            setIsScanning(false);
            return;
          }
        }
      }

      // Create barcode reader (supports both QR codes and barcodes including Code 128)
      const codeReader = new BrowserMultiFormatReader();
      codeReaderRef.current = codeReader;

      // Get available video input devices
      let videoInputDevices = [];
      try {
        videoInputDevices = await codeReader.listVideoInputDevices();
        console.log('Available cameras:', videoInputDevices.map(d => d.label));
      } catch (err) {
        console.log('Could not list video devices:', err);
      }

      // Prefer back camera (environment) if available
      let selectedDeviceId = null;
      if (videoInputDevices.length > 0) {
        for (const device of videoInputDevices) {
          const label = device.label.toLowerCase();
          if (label.includes('back') ||
            label.includes('rear') ||
            label.includes('environment') ||
            label.includes('facing back')) {
            selectedDeviceId = device.deviceId;
            console.log('Selected back camera:', device.label);
            break;
          }
        }
        // If no back camera found, use first available
        if (!selectedDeviceId) {
          selectedDeviceId = videoInputDevices[0].deviceId;
          console.log('Using first available camera:', videoInputDevices[0].label);
        }
      } else {
        console.log('No video devices found, using default');
      }

      // Ensure video element is ready
      if (!videoRef.current) {
        throw new Error('Video element not available');
      }

      console.log('Starting barcode scanner with device:', selectedDeviceId || 'default');

      // Wait a bit to ensure video element is ready
      await new Promise(resolve => setTimeout(resolve, 300));

      // Start decoding from video device (supports barcodes and QR codes including Code 128)
      // decodeFromVideoDevice automatically handles the video stream
      try {
        codeReader.decodeFromVideoDevice(
          selectedDeviceId || undefined, // Use undefined for default device
          videoRef.current,
          (result, err) => {
            if (result) {
              // Barcode/QR code detected
              const barcodeData = result.getText();
              const format = result.getBarcodeFormat();
              console.log('✅ Barcode detected!', {
                text: barcodeData,
                format: format ? format.toString() : 'unknown'
              });
              if (barcodeData) {
                // Stop scanning and process the barcode
                if (codeReaderRef.current) {
                  codeReaderRef.current.reset();
                }
                handleQRCodeDetected(barcodeData);
              }
            }
            if (err) {
              // Ignore NotFoundError - it's just scanning, no code found yet
              if (err.name !== 'NotFoundException' && err.name !== 'NoQRCodeFound') {
                // Only log non-scanning errors
                if (err.name !== 'ChecksumException' && err.name !== 'FormatException') {
                  console.log('Scanning...', err.name);
                }
              }
            }
          }
        );
        console.log('Barcode scanner started successfully');

        // Wait a moment for the scanner to initialize
        await new Promise(resolve => setTimeout(resolve, 500));

        // Check if video stream is attached
        if (videoRef.current && videoRef.current.srcObject) {
          console.log('Video stream attached successfully');
          streamRef.current = videoRef.current.srcObject;
        } else {
          console.warn('Video stream not yet attached, waiting...');
          // Wait a bit more for stream to attach
          await new Promise(resolve => setTimeout(resolve, 1000));
          if (videoRef.current && videoRef.current.srcObject) {
            streamRef.current = videoRef.current.srcObject;
            console.log('Video stream attached after wait');
          }
        }
      } catch (scanError) {
        console.error('Error starting scanner:', scanError);
        throw scanError;
      }

      setIsLoading(false);
    } catch (err) {
      console.error('Camera error:', err);

      let errorMessage = 'Camera access denied. Please allow camera access and try again.';

      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        errorMessage = 'Camera permission denied. Please allow camera access in your browser settings and try again.';
      } else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
        errorMessage = 'No camera found. Please use a device with a camera.';
      } else if (err.name === 'NotReadableError' || err.name === 'TrackStartError') {
        errorMessage = 'Camera is already in use by another application. Please close other apps using the camera.';
      } else if (err.name === 'OverconstrainedError' || err.name === 'ConstraintNotSatisfiedError') {
        errorMessage = 'Camera constraints could not be satisfied. Please try again.';
      } else if (err.message) {
        errorMessage = err.message;
      }

      if (navigator.userAgent.includes('Chrome') && (errorMessage.includes('permission') || errorMessage.includes('denied') || err.name === 'NotAllowedError')) {
        errorMessage += ' On Chrome: Tap the lock icon (🔒) in the address bar -> Site Settings -> Camera -> Allow.';
      }

      setError(errorMessage);
      setIsLoading(false);
      setIsScanning(false);
      if (videoRef.current) {
        videoRef.current.style.display = 'none';
      }
    }
  };

  const stopCamera = () => {
    if (codeReaderRef.current) {
      try {
        codeReaderRef.current.reset();
      } catch (e) {
        console.log('Error resetting scanner:', e);
      }
      codeReaderRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      // Stop any video tracks
      if (videoRef.current.srcObject) {
        const stream = videoRef.current.srcObject;
        stream.getTracks().forEach(track => track.stop());
        videoRef.current.srcObject = null;
      }
      videoRef.current.style.display = 'none';
      videoRef.current.style.visibility = 'hidden';
    }
    setIsScanning(false);
  };

  // Create or update order when frame is selected.
  //
  // Stock-lens reservations are issued automatically by the server when
  // POST /api/orders sees an `intern` lens line — there is nothing to
  // call from the client. The order id (saved to sessionStorage below)
  // is the persistence key for the reservation; cancelling the order
  // (status=cancelled) releases every claim attached to it. See
  // server/utils/reservations.js for the lifecycle.
  const createOrderOnFrameSelection = async (frame) => {
    console.log('🔄 Creating order on frame selection...', frame);
    try {
      const visitorId = sessionStorage.getItem('visitorId');
      const prescriptionId = sessionStorage.getItem('prescriptionId');

      console.log('📋 Visitor ID:', visitorId);
      console.log('📋 Prescription ID:', prescriptionId);

      if (!visitorId) {
        console.error('❌ No visitor ID found, cannot create order yet');
        alert('Warning: No visitor ID found. Order will be created later.');
        return;
      }

      // Check if order already exists
      const existingOrderId = sessionStorage.getItem('orderId');
      console.log('📋 Existing Order ID:', existingOrderId);

      const lensPrice = 12000;
      const framePrice = frame?.price || 0;
      const subtotal = framePrice + lensPrice;
      const tax = Math.round(subtotal * 0.1); // 10% tax
      const total = subtotal + tax;

      const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
      const orderData = {
        visitor: visitorId,
        prescription: prescriptionId || null,
        store: storedUser?.store?.id || storedUser?.store?._id || storedUser?.storeId || null,
        frameAdjustmentsVerified: buildFrameAdjustmentsPayload(),
        items: [
          ...(frame && !frame.hasOwnFrame ? [{
            type: 'frame',
            name: frame.name || 'Frame',
            brand: frame.brand || '',
            model: frame.model || '',
            material: frame.material || '',
            color: frame.color || '',
            quantity: 1,
            unitPrice: framePrice,
            totalPrice: framePrice
          }] : []),
          {
            type: 'lens',
            name: 'Prescription Lenses',
            brand: 'Optiva',
            material: 'Polycarbonate',
            quantity: 1,
            unitPrice: lensPrice,
            totalPrice: lensPrice
          }
        ],
        subtotal: subtotal,
        tax: tax,
        discount: 0,
        total: total,
        payment: {
          method: 'cash',
          status: 'pending',
          amount: 0
        },
        status: 'confirmed',
        reservationDraft: false
      };

      console.log('📦 Order Data:', JSON.stringify(orderData, null, 2));

      const token = localStorage.getItem('token');
      if (!token) {
        console.error('❌ No auth token, order will be created later');
        alert('Warning: No authentication token found. Please log in again.');
        return;
      }

      let orderResponse;
      if (existingOrderId) {
        // Update existing order
        console.log('🔄 Updating existing order:', existingOrderId);
        try {
          orderResponse = await axios.put(`/api/orders/${existingOrderId}`, orderData, {
            headers: { Authorization: `Bearer ${token}` }
          });
          console.log('✅ Order updated successfully:', orderResponse.data);
          // Handle both response.data and response.data.order
          const order = orderResponse.data.order || orderResponse.data;
          sessionStorage.setItem('orderData', JSON.stringify(order));
        } catch (updateError) {
          // If update fails, try creating new order
          console.warn('⚠️ Update failed, creating new order:', updateError.response?.data || updateError.message);
          orderResponse = await axios.post('/api/orders', orderData, {
            headers: { Authorization: `Bearer ${token}` }
          });
          console.log('✅ New order created after update failed:', orderResponse.data);
          const order = orderResponse.data;
          sessionStorage.setItem('orderId', order._id);
          sessionStorage.setItem('orderNumber', order.orderNumber);
          sessionStorage.setItem('orderData', JSON.stringify(order));
        }
      } else {
        // Create new order
        console.log('🆕 Creating new order...');
        orderResponse = await axios.post('/api/orders', orderData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        console.log('✅ Order created successfully:', orderResponse.data);
        const order = orderResponse.data;
        sessionStorage.setItem('orderId', order._id);
        sessionStorage.setItem('orderNumber', order.orderNumber);
        sessionStorage.setItem('orderData', JSON.stringify(order));
        console.log('💾 Order saved to sessionStorage');
      }

      console.log('✅ Order draft saved successfully');
    } catch (error) {
      console.error('❌ Error creating order on frame selection:', error);
      console.error('❌ Error response:', error.response?.data);
      console.error('❌ Error status:', error.response?.status);
      setError(t('frameSelection.saveOrderError', { msg: error.response?.data?.message || error.message }));
      // Don't block user flow if order creation fails, but show error
    }
  };

  const fetchFrameByCode = async (barcodeCode) => {
    try {
      const response = await axios.get(`/api/frames/qr/${encodeURIComponent(barcodeCode)}`);

      if (response.data.success && response.data.frame) {
        const frame = response.data.frame;

        if (isAddingExtraFrame) {
          // Extra frames are simple frame-only add-ons — no lens/adjustments,
          // just added to the list, so an out-of-stock one is refused outright
          // rather than added with a warning banner (there's no per-row banner
          // for the list, unlike the primary frame's card).
          if (isFrameOutOfStock(frame)) {
            setError(t('frameSelection.outOfStock', { name: frame.name }));
            setSuccess('');
          } else {
            setExtraFrames((prev) => [...prev, frame]);
            setSuccess(t('frameSelection.extraFrames.added', { name: frameDisplayName(frame.name) }));
            setError('');
          }
          setManualCode('');
          setShowManualInput(false);
          setIsAddingExtraFrame(false);
          return true;
        }

        setSelectedFrame(frame);
        setHasOwnFrame(false); // Clear "has own frame" when a frame is selected
        if (isFrameOutOfStock(frame)) {
          // Out of stock: don't show the green "found" confirmation — the red
          // out-of-stock banner + disabled Continue handle it.
          setSuccess('');
        } else {
          setSuccess(t('frameSelection.frameFound'));
        }
        setError('');
        setManualCode(''); // Clear manual input
        setShowManualInput(false); // Hide manual input

        // Order is created at payment stage, not here

        return true;
      } else {
        setError('Frame not found with this barcode.');
        setSuccess('');
        return false;
      }
    } catch (err) {
      console.error('Error fetching frame:', err);
      if (err.response?.status === 404) {
        const errorMsg = err.response?.data?.details || 'Frame not found with this barcode. Please enter a valid frame barcode.';
        setError(errorMsg);
      } else {
        setError('Failed to load frame information. Please try again.');
      }
      setSuccess('');
      return false;
    }
  };

  const handleQRCodeDetected = async (barcodeData) => {
    setSuccess('Frame barcode detected! Loading frame information...');
    setIsLoading(true);
    stopCamera();

    try {
      // Clean and normalize the scanned barcode data
      const cleanedBarcode = String(barcodeData || '').trim().toUpperCase();

      if (!cleanedBarcode) {
        setError('Invalid barcode. No data detected.');
        setIsLoading(false);
        return;
      }

      console.log('Scanning barcode:', cleanedBarcode);
      await fetchFrameByCode(cleanedBarcode);
    } catch (err) {
      console.error('Error in handleQRCodeDetected:', err);
      setError('Failed to process barcode. Please try again.');
      setSuccess('');
    } finally {
      setIsLoading(false);
    }
  };

  const handleManualSubmit = async (e) => {
    e.preventDefault();
    if (!manualCode.trim()) {
      setError('Please enter a barcode code.');
      return;
    }

    setIsLoading(true);
    setError('');
    setSuccess('Searching for frame...');

    try {
      const cleanedCode = manualCode.trim().toUpperCase();
      await fetchFrameByCode(cleanedCode);
    } catch (err) {
      // Error already handled in fetchFrameByCode
    } finally {
      setIsLoading(false);
    }
  };

  const handleNext = async () => {
    // Allow proceeding if client has own frame, otherwise require frame selection
    if (!selectedFrame && !hasOwnFrame) {
      alert('Please scan a frame barcode or select "Client has own frame" to continue');
      return;
    }

    if (isOwnFrameRejected) {
      alert('This client frame is rejected and cannot proceed.');
      return;
    }

    if (isFrameOutOfStock(selectedFrame)) {
      alert(t('frameSelection.outOfStock', { name: selectedFrame.name }));
      return;
    }

    if (!allAdjustmentsChecked) {
      alert(
        'Please confirm all optical adjustments: vertex distance, pantoscopic angle, wrap angle, temple, and nose bridge.'
      );
      return;
    }

    if (hasOwnFrame) {
      // Store flag indicating client has own frame
      const ownFrameData = {
        hasOwnFrame: true,
        name: OWN_FRAME_NAME,   // canonical; translated only when rendered
        price: 0,
        lensPrice: lensPrice,
        totalPrice: lensPrice
      };
      sessionStorage.setItem(k('selectedFrame'), JSON.stringify(ownFrameData));

      // Order created at payment stage
    } else {
      // Store full frame selection in session storage
      const frameData = {
        id: selectedFrame._id || selectedFrame.id,
        name: selectedFrame.name,
        brand: selectedFrame.brand || '',
        model: selectedFrame.model || '',
        material: selectedFrame.material || '',
        color: selectedFrame.color || '',
        shape: selectedFrame.shape || '',
        size: selectedFrame.size || '',
        sex: selectedFrame.sex || '',
        price: selectedFrame.price,
        lensPrice: lensPrice,
        totalPrice: lensPrice + selectedFrame.price,
        hasOwnFrame: false
      };
      sessionStorage.setItem(k('selectedFrame'), JSON.stringify(frameData));

      // Order will be created at payment stage
    }

    const orderId = sessionStorage.getItem(k('orderId'));
    const token = localStorage.getItem('token');
    if (orderId && token) {
      try {
        await axios.put(
          `/api/orders/${orderId}`,
          { frameAdjustmentsVerified: buildFrameAdjustmentsPayload() },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } catch (e) {
        console.error('Failed to save frame adjustments on order:', e);
      }
    }

    if (!embedded) navigate('/measurement');
    return true;
  };

  // Track page visibility to mark order incomplete if user leaves
  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.hidden) {
        // User left the page - mark order as incomplete if it exists
        const orderId = sessionStorage.getItem(k('orderId'));
        if (orderId) {
          try {
            const token = localStorage.getItem('token');
            if (token) {
              await axios.put(`/api/orders/${orderId}/status`, {
                status: 'pending',
                incompleteReason: 'User left frame selection page before completing order'
              }, {
                headers: { Authorization: `Bearer ${token}` }
              });
            }
          } catch (error) {
            console.error('Error marking order incomplete:', error);
          }
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      stopCamera();
    };
  }, []);

  // Tracking order functionality removed - use Track Order page in sidebar instead

  // Shared by the primary frame slot and the "extra frame" add flow — only one
  // scan/manual-entry session can be active at a time, so both reuse the same
  // camera + form state (isScanning/manualCode/etc.) and just differ in what
  // fetchFrameByCode does with the frame it finds (isAddingExtraFrame).
  const renderFrameLookupUI = () => (
    <>
      <SectionHelper>
        {t('frameSelection.scanInstruction')}
      </SectionHelper>

      <ScannerContainer
        isScanning={isScanning}
        onClick={!isScanning ? startCamera : undefined}
      >
        <Video
          ref={videoRef}
          isScanning={isScanning}
        />
        {!isScanning && (
          <ScannerPlaceholder>
            <ScannerIcon>
              <QrCode size={28} />
            </ScannerIcon>
            <ScannerText>{t('frameSelection.tapToScan')}</ScannerText>
            <ScannerSubtext>{t('frameSelection.holdSteady')}</ScannerSubtext>
          </ScannerPlaceholder>
        )}
        {isScanning && isLoading && (
          <ScannerPlaceholder style={{ position: 'absolute', background: 'rgba(0,0,0,0.5)', color: 'white' }}>
            <Camera size={28} style={{ animation: 'pulse 1s infinite' }} />
            <ScannerText style={{ color: 'white' }}>{t('frameSelection.startingCamera')}</ScannerText>
          </ScannerPlaceholder>
        )}
      </ScannerContainer>

      {isScanning && (
        <StopButton onClick={stopCamera}>
          {t('frameSelection.stopScanning')}
        </StopButton>
      )}

      {!showManualInput ? (
        <ToggleManualButton onClick={() => setShowManualInput(true)}>
          {t('frameSelection.enterManually')}
        </ToggleManualButton>
      ) : (
        <ManualInputContainer>
          <ManualInputRow onSubmit={handleManualSubmit}>
            <ManualInput
              type="text"
              value={manualCode}
              onChange={(e) => setManualCode(e.target.value.toUpperCase())}
              placeholder={t('frameSelection.eGFrm12345678901')}
              disabled={isLoading}
              autoFocus
            />
            <ManualInputButton type="submit" disabled={isLoading || !manualCode.trim()}>
              {isLoading ? t('frameSelection.loading') : t('frameSelection.search')}
            </ManualInputButton>
          </ManualInputRow>
          <ToggleManualButton
            type="button"
            onClick={() => {
              setShowManualInput(false);
              setManualCode('');
              setError('');
            }}
            style={{ marginTop: '0.5rem' }}
          >
            {t('frameSelection.cancel')}
          </ToggleManualButton>
        </ManualInputContainer>
      )}
    </>
  );

  const canContinue = !((!selectedFrame && !hasOwnFrame) || !allAdjustmentsChecked || isOwnFrameRejected || isFrameOutOfStock(selectedFrame));
  useImperativeHandle(ref, () => ({ submit: handleNext }));
  useEffect(() => {
    if (onReadyChange) onReadyChange(pair, canContinue);
  }, [onReadyChange, pair, canContinue]);

  const Shell = embedded ? React.Fragment : Container;
  return (
    <Shell>
      {!embedded && (
        <Header>
          <BackButton onClick={() => navigate('/prescription')}>
            <ArrowLeft size={16} />
            {t('frameSelection.backToPrescription')}
          </BackButton>
        </Header>
      )}

      <PageContent>
        <ContentGrid>
        <ScannerSection>
          <CardHeaderRow>
            <CardTitle>{t('frameSelection.cardTitle')}</CardTitle>
            {selectedFrame && (
              <StatusPill $tone="success"><CheckCircle size={12} /> {t('frameSelection.frameSelected')}</StatusPill>
            )}
            {!selectedFrame && hasOwnFrame && (
              <StatusPill $tone="info"><User size={12} /> {t('frameSelection.clientOwnFrame')}</StatusPill>
            )}
            {!selectedFrame && !hasOwnFrame && (
              <StatusPill><QrCode size={12} /> {t('frameSelection.awaitingScan')}</StatusPill>
            )}
          </CardHeaderRow>

          {error && (
            <ErrorMessage>
              <AlertCircle size={18} />
              {error}
            </ErrorMessage>
          )}

          {success && (
            <SuccessMessage>
              <CheckCircle size={18} />
              {success}
            </SuccessMessage>
          )}

          {/* ── Frame scanned: show details + "scan another" ─────────────────── */}
          {selectedFrame ? (
            <>
              {isFrameOutOfStock(selectedFrame) && (
                <OutOfStockBanner>
                  <AlertCircle size={18} style={{ flexShrink: 0, marginTop: '1px' }} />
                  <div>
                    <strong>{t('frameSelection.outOfStockTitle')}</strong>
                    {t('frameSelection.outOfStock', { name: selectedFrame.name })}
                  </div>
                </OutOfStockBanner>
              )}
              {(() => {
                let gender = null, ageGroup = null;
                try {
                  const v = JSON.parse(sessionStorage.getItem('visitorData') || '{}');
                  gender = v.gender || null;
                  ageGroup = v.ageGroup || null;
                } catch (_) { /* no visit data */ }
                const warnKey = frameFitWarningKey(selectedFrame, gender, ageGroup);
                return warnKey ? (
                  <FitWarning>
                    <AlertCircle size={18} style={{ flexShrink: 0, marginTop: '1px' }} />
                    <div>
                      <strong>{t('frameSelection.fitWarning.title')}</strong>
                      {t(warnKey)}
                    </div>
                  </FitWarning>
                ) : null;
              })()}
              <FrameDetailsGrid>
                <FrameDetail>
                  <FrameDetailLabel>{t('frameSelection.name')}</FrameDetailLabel>
                  <FrameDetailValue>{frameDisplayName(selectedFrame.name)}</FrameDetailValue>
                </FrameDetail>
                {selectedFrame.brand && (
                  <FrameDetail>
                    <FrameDetailLabel>{t('frameSelection.brand')}</FrameDetailLabel>
                    <FrameDetailValue>{selectedFrame.brand}</FrameDetailValue>
                  </FrameDetail>
                )}
                {selectedFrame.model && (
                  <FrameDetail>
                    <FrameDetailLabel>{t('frameSelection.model')}</FrameDetailLabel>
                    <FrameDetailValue>{selectedFrame.model}</FrameDetailValue>
                  </FrameDetail>
                )}
                {selectedFrame.material && (
                  <FrameDetail>
                    <FrameDetailLabel>{t('frameSelection.material')}</FrameDetailLabel>
                    <FrameDetailValue>{selectedFrame.material}</FrameDetailValue>
                  </FrameDetail>
                )}
                {selectedFrame.shape && (
                  <FrameDetail>
                    <FrameDetailLabel>{t('frameSelection.shape')}</FrameDetailLabel>
                    <FrameDetailValue>{selectedFrame.shape.replace('_', ' ')}</FrameDetailValue>
                  </FrameDetail>
                )}
                {selectedFrame.color && (
                  <FrameDetail>
                    <FrameDetailLabel>{t('frameSelection.color')}</FrameDetailLabel>
                    <FrameDetailValue>{selectedFrame.color}</FrameDetailValue>
                  </FrameDetail>
                )}
                {selectedFrame.size && (
                  <FrameDetail>
                    <FrameDetailLabel>{t('frameSelection.size')}</FrameDetailLabel>
                    <FrameDetailValue>{selectedFrame.size}</FrameDetailValue>
                  </FrameDetail>
                )}
                {selectedFrame.sex && (
                  <FrameDetail>
                    <FrameDetailLabel>{t('frameSelection.sex')}</FrameDetailLabel>
                    <FrameDetailValue>{selectedFrame.sex.charAt(0).toUpperCase() + selectedFrame.sex.slice(1)}</FrameDetailValue>
                  </FrameDetail>
                )}
              </FrameDetailsGrid>

              <FramePrice>{selectedFrame.price?.toLocaleString()} {t('frameSelection.dzd')}</FramePrice>

              {selectedFrame.description && (
                <FrameDetail style={{ marginTop: '0.5rem' }}>
                  <FrameDetailLabel>{t('frameSelection.description')}</FrameDetailLabel>
                  <FrameDetailValue>{selectedFrame.description}</FrameDetailValue>
                </FrameDetail>
              )}

              <div style={{ display: 'flex', justifyContent: 'center', marginTop: '0.75rem' }}>
                <ScanAnotherButton
                  type="button"
                  onClick={() => {
                    setSelectedFrame(null);
                    setError('');
                    setSuccess('');
                    setManualCode('');
                    setShowManualInput(false);
                  }}
                >
                  <QrCode size={14} /> {t('frameSelection.scanAnother')}
                </ScanAnotherButton>
              </div>
            </>
          ) : !hasOwnFrame ? (
            /* ── No frame, no own-frame: show scanner + manual input ─────────── */
            renderFrameLookupUI()
          ) : null}

          {/* ── Client own-frame card (always visible, alternative path) ────── */}
          <OwnFrameCard $active={hasOwnFrame}>
            <OwnFrameToggle
              type="button"
              onClick={() => {
                const next = !hasOwnFrame;
                setHasOwnFrame(next);
                if (next) {
                  setSelectedFrame(null);
                  setError('');
                  setSuccess('');
                }
              }}
            >
              <OwnFrameToggleIcon $active={hasOwnFrame}>
                <User size={16} />
              </OwnFrameToggleIcon>
              <OwnFrameToggleText>
                <strong>{t('frameSelection.clientOwnFrame')}</strong>
                <span>{hasOwnFrame
                  ? t('frameSelection.ownFrameOnlyLens')
                  : t('frameSelection.ownFrameHint')}</span>
              </OwnFrameToggleText>
              <OwnFrameSwitch $active={hasOwnFrame} aria-hidden />
            </OwnFrameToggle>

            {hasOwnFrame && (
              <OwnFrameDetails>
                <ConditionSection style={{ marginTop: 0 }}>
                  <ConditionTitle>{t('frameSelection.clientFrameGeneralCondition')}</ConditionTitle>
                  <ConditionList>
                    <ConditionHeader>
                      <span>{t('frameSelection.yes')}</span>
                      <span>{t('frameSelection.frameAspectAndGeneralCondition')}</span>
                      <span>{t('frameSelection.no')}</span>
                    </ConditionHeader>
                    {frameConditionChecks.map((check) => (
                      <ConditionChoiceRow key={check.key}>
                        <ChoiceButton
                          type="button"
                          $active={ownFrameChecks[check.key] === true}
                          onClick={() => setOwnFrameChecks((prev) => ({ ...prev, [check.key]: true }))}
                        >
                          {t('frameSelection.yes')}
                        </ChoiceButton>
                        <ConditionLabel>
                          <span>{check.label}</span>
                        {check.critical && <CriticalBadge>{t('frameSelection.critical')}</CriticalBadge>}
                        </ConditionLabel>
                        <ChoiceButton
                          type="button"
                          $active={ownFrameChecks[check.key] === false}
                          onClick={() => setOwnFrameChecks((prev) => ({ ...prev, [check.key]: false }))}
                        >
                          {t('frameSelection.no')}
                        </ChoiceButton>
                      </ConditionChoiceRow>
                    ))}
                  </ConditionList>
                  {(() => {
                    const rejectPercent = calculateOwnFrameRisk();
                    if (rejectPercent === 0) return null;
                    if (rejectPercent <= 40) {
                      return (
                        <RiskBanner>
                          <strong>{t('frameSelection.acceptableWithRiskOfBreakage')}</strong>
                          <div style={{ marginTop: '0.25rem' }}>{t('frameSelection.riskPercentage')} {rejectPercent}%</div>
                        </RiskBanner>
                      );
                    }
                    if (rejectPercent <= 55) {
                      return (
                        <HighRiskBanner>
                          <strong>{t('frameSelection.highRiskOfBreakage')}</strong>
                          <div style={{ marginTop: '0.25rem' }}>
                            {t('frameSelection.frameMayBeUsedOnly')} {rejectPercent}%
                          </div>
                        </HighRiskBanner>
                      );
                    }
                    return (
                      <RejectBanner>
                        <strong>{t('frameSelection.rejectFrame')}</strong>
                        <div style={{ marginTop: '0.25rem' }}>{t('frameSelection.riskPercentage')} {rejectPercent}{t('frameSelection.thisFrameCannotBeUsed')}</div>
                      </RejectBanner>
                    );
                  })()}
                </ConditionSection>
              </OwnFrameDetails>
            )}
          </OwnFrameCard>
        </ScannerSection>

        {/* ── Extra frames: frame-only add-ons, no lens attached ─────────────── */}
        <ExtraFramesCard>
          <CardHeaderRow>
            <CardTitle>{t('frameSelection.extraFrames.title')}</CardTitle>
          </CardHeaderRow>
          <SectionHelper>{t('frameSelection.extraFrames.hint')}</SectionHelper>

          {extraFrames.length > 0 && (
            <ExtraFrameList>
              {extraFrames.map((frame, index) => (
                <ExtraFrameRow key={`${frame._id || frame.id || frame.name}-${index}`}>
                  <ExtraFrameInfo>
                    <ExtraFrameName>{frameDisplayName(frame.name)}</ExtraFrameName>
                    {(frame.brand || frame.model) && (
                      <ExtraFrameMeta>{[frame.brand, frame.model].filter(Boolean).join(' · ')}</ExtraFrameMeta>
                    )}
                  </ExtraFrameInfo>
                  <ExtraFramePrice>{frame.price?.toLocaleString()} {t('frameSelection.dzd')}</ExtraFramePrice>
                  <RemoveExtraFrameButton
                    type="button"
                    onClick={() => removeExtraFrame(index)}
                    aria-label={t('frameSelection.extraFrames.remove')}
                  >
                    <X size={14} />
                  </RemoveExtraFrameButton>
                </ExtraFrameRow>
              ))}
            </ExtraFrameList>
          )}

          {isAddingExtraFrame ? (
            <>
              {renderFrameLookupUI()}
              <ToggleManualButton
                type="button"
                onClick={() => {
                  setIsAddingExtraFrame(false);
                  setShowManualInput(false);
                  setManualCode('');
                  setError('');
                  setSuccess('');
                  stopCamera();
                }}
                style={{ marginTop: '0.5rem' }}
              >
                {t('frameSelection.cancel')}
              </ToggleManualButton>
            </>
          ) : (
            <AddExtraFrameButton
              type="button"
              onClick={() => {
                setIsAddingExtraFrame(true);
                setError('');
                setSuccess('');
              }}
            >
              <Plus size={16} /> {t('frameSelection.extraFrames.add')}
            </AddExtraFrameButton>
          )}
        </ExtraFramesCard>
        </ContentGrid>

        <AdjustmentsRow aria-labelledby={`optical-adjustments-heading-${pair}`}>
          <AdjustmentsPanel>
            <AdjustmentsInlineBar>
              <AdjustmentsTitle id={`optical-adjustments-heading-${pair}`}>{t('frameSelection.opticalAdjustments')}</AdjustmentsTitle>
              <AdjustmentsChecks>
                <AdjustmentField>
                  <AdjustmentLabel htmlFor={`adj-vertex-${pair}`}>{t('frameSelection.adj.vertexDistance')}</AdjustmentLabel>
                  <AdjustmentInput
                    id={`adj-vertex-${pair}`}
                    type="text"
                    inputMode="numeric"
                    maxLength={2}
                    placeholder={t('frameSelection.x1214mm')}
                    value={adjustmentValues.vertexDistance}
                    onChange={(e) => handleAdjustmentValueChange('vertexDistance', e.target.value)}
                    $invalid={isAdjustmentOutOfRange('vertexDistance')}
                  />
                  {isAdjustmentOutOfRange('vertexDistance') && (
                    <AdjustmentWarning>
                      {t('frameSelection.adj.recommendedRange', ADJ_RANGES.vertexDistance)}
                    </AdjustmentWarning>
                  )}
                </AdjustmentField>

                <AdjustmentField>
                  <AdjustmentLabel htmlFor={`adj-panto-${pair}`}>{t('frameSelection.adj.pantoscopicAngle')}</AdjustmentLabel>
                  <AdjustmentInput
                    id={`adj-panto-${pair}`}
                    type="text"
                    inputMode="numeric"
                    maxLength={2}
                    placeholder="8 - 12°"
                    value={adjustmentValues.pantoscopicAngle}
                    onChange={(e) => handleAdjustmentValueChange('pantoscopicAngle', e.target.value)}
                    $invalid={isAdjustmentOutOfRange('pantoscopicAngle')}
                  />
                  {isAdjustmentOutOfRange('pantoscopicAngle') && (
                    <AdjustmentWarning>
                      {t('frameSelection.adj.recommendedRange', ADJ_RANGES.pantoscopicAngle)}
                    </AdjustmentWarning>
                  )}
                </AdjustmentField>

                <AdjustmentField>
                  <AdjustmentLabel htmlFor={`adj-wrap-${pair}`}>{t('frameSelection.adj.wrapAngle')}</AdjustmentLabel>
                  <AdjustmentInput
                    id={`adj-wrap-${pair}`}
                    type="text"
                    inputMode="numeric"
                    maxLength={2}
                    placeholder="0 - 9°"
                    value={adjustmentValues.wrapAngleGalbe}
                    onChange={(e) => handleAdjustmentValueChange('wrapAngleGalbe', e.target.value)}
                    $invalid={isAdjustmentOutOfRange('wrapAngleGalbe')}
                  />
                  {isAdjustmentOutOfRange('wrapAngleGalbe') && (
                    <AdjustmentWarning>
                      {t('frameSelection.adj.recommendedRange', ADJ_RANGES.wrapAngleGalbe)}
                    </AdjustmentWarning>
                  )}
                </AdjustmentField>

                <AdjustmentField>
                  <AdjustmentLabel>{t('frameSelection.adj.temple')}</AdjustmentLabel>
                  <AdjustToggle $checked={!!adjustmentChecks.branch}>
                    <input
                      type="checkbox"
                      checked={!!adjustmentChecks.branch}
                      onChange={(e) => setAdjustmentChecks((prev) => ({ ...prev, branch: e.target.checked }))}
                    />
                    {adjustmentChecks.branch && <CheckCircle size={14} />}
                    <span>{t('frameSelection.adj.adjust')}</span>
                  </AdjustToggle>
                </AdjustmentField>

                <AdjustmentField>
                  <AdjustmentLabel>{t('frameSelection.adj.noseBridge')}</AdjustmentLabel>
                  <AdjustToggle $checked={!!adjustmentChecks.nez}>
                    <input
                      type="checkbox"
                      checked={!!adjustmentChecks.nez}
                      onChange={(e) => setAdjustmentChecks((prev) => ({ ...prev, nez: e.target.checked }))}
                    />
                    {adjustmentChecks.nez && <CheckCircle size={14} />}
                    <span>{t('frameSelection.adj.adjust')}</span>
                  </AdjustToggle>
                </AdjustmentField>
              </AdjustmentsChecks>
            </AdjustmentsInlineBar>
          </AdjustmentsPanel>
        </AdjustmentsRow>

        {!embedded && (
          <ContinueRow>
            <NextButton onClick={handleNext} disabled={!canContinue}>
              <span>{t('frameSelection.continueToMeasures')}</span>
              <ArrowRight size={18} />
            </NextButton>
          </ContinueRow>
        )}
      </PageContent>
    </Shell>
  );
});

const readLensName = (key) => {
  try {
    const lens = JSON.parse(sessionStorage.getItem(key) || 'null');
    return lens ? (lens.commercialName || lens.name || '') : '';
  } catch (_) { return ''; }
};

// The frame step. A two-pair sale picks both frames side by side: pair 1 for
// far vision, pair 2 for close up, then one Continue for both.
const FrameSelection = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const farRef = useRef(null);
  const nearRef = useRef(null);
  const [ready, setReady] = useState({ far: false, near: false });
  const onReadyChange = React.useCallback((pair, ok) => {
    setReady((prev) => (prev[pair] === ok ? prev : { ...prev, [pair]: ok }));
  }, []);

  if (!isTwoPairSale()) return <FramePanel pair="far" />;

  const handleNextBoth = async () => {
    if (!(await farRef.current?.submit())) return;
    if (!(await nearRef.current?.submit())) return;
    navigate('/measurement');
  };

  return (
    <Container>
      <Header>
        <BackButton onClick={() => navigate('/prescription')}>
          <ArrowLeft size={16} />
          {t('frameSelection.backToPrescription')}
        </BackButton>
      </Header>
      <PairColumns>
        {[['far', farRef, 'selectedLens'], ['near', nearRef, 'selectedLensNear']].map(([pair, pairRef, lensKey]) => (
          <PairColumn key={pair}>
            <PairColumnHead>
              <PairBanner>{t(pair === 'near' ? 'pairs.pairNear' : 'pairs.pairFar')}</PairBanner>
              <span>{readLensName(lensKey)}</span>
            </PairColumnHead>
            <FramePanel ref={pairRef} pair={pair} embedded onReadyChange={onReadyChange} />
          </PairColumn>
        ))}
      </PairColumns>
      <PairContinueRow>
        <NextButton onClick={handleNextBoth} disabled={!ready.far || !ready.near}>
          <span>{t('frameSelection.continueToMeasures')}</span>
          <ArrowRight size={18} />
        </NextButton>
      </PairContinueRow>
    </Container>
  );
};

export default FrameSelection;
